import { Button, Form, InputNumber, Space } from 'antd'
import { FC, useMemo, useRef } from 'react'
import useErrorModal from '../../../../hooks/api/use-error-modal'
import useSuccessModal from '../../../../hooks/api/use-success-modal'
import { useGetMaterialTypesQuery } from '../../../../store/api/material-types-api'
import { useSubmitConsumptionMutation } from '../../../../store/api/print-jobs-api'
import PrintJob from '../../../../types/api/print-job'
import {
    computeMachineCounterAfterSave,
    getSavedConsumptionMeters,
    getUniqueMachines,
    hasSavedMaterialConsumptions,
    materialLengthKmToMeters,
    roundAmountKm,
    sumMaterialConsumptionLengthKm,
    toMeters,
} from '../../helpers/consumption-helpers'
import {
    getValidMaterialConsumptions,
    MaterialConsumptionFormValue,
} from '../../helpers/consumption-form-values'
import HiddenMachineCountersFields from './hidden-machine-counters-field/hidden-machine-counters-field'
import MachineCounterPreview from './machine-counter-preview/machine-counter-preview'
import MaterialsList from './material-list/material-list'
import { ConsumptionFormValues } from './types'

interface ConsumptionFormProps {
    printJob: PrintJob
    onSuccess: () => void
    onClose: () => void
}

function getInitialValues(printJob: PrintJob): ConsumptionFormValues {
    const machines = getUniqueMachines(printJob)

    return {
        actualFloods: printJob.actualFloods,
        materials: [{} as MaterialConsumptionFormValue],
        machineCounters: machines.map(machine => ({
            machineDocumentId: machine.documentId,
            machineName: machine.name,
            currentCounter: toMeters(machine.machineCounter),
        })),
        savedMachineCounter: toMeters(printJob.machineCounter),
        initialConsumptionMeters: getSavedConsumptionMeters(printJob),
    }
}

function canEdit(printJob: PrintJob): boolean {
    return !hasSavedMaterialConsumptions(printJob)
}

const ConsumptionForm: FC<ConsumptionFormProps> = ({ printJob, onSuccess, onClose }) => {
    const [form] = Form.useForm<ConsumptionFormValues>()
    const editable = canEdit(printJob)
    const initialConsumptionMetersRef = useRef(getSavedConsumptionMeters(printJob))
    const printJobMachineCounterRef = useRef(toMeters(printJob.machineCounter))
    const [submitConsumption, submitState] = useSubmitConsumptionMutation()

    const { data: typesData } = useGetMaterialTypesQuery({
        populate: { materials: true },
        pagination: { pageSize: 200, page: 1 },
    })

    useErrorModal(submitState, 'Не удалось сохранить расход')
    useSuccessModal(submitState, 'Расход сохранён')

    const types = typesData?.data || []
    const typeOptions = useMemo(
        () => types.map(type => ({ value: type.documentId, label: type.name })),
        [types]
    )

    const saveConsumption = async (values: ConsumptionFormValues, machineCounter?: number) => {
        const items = getValidMaterialConsumptions(values.materials).map(item => ({
            materialDocumentId: item.materialDocumentId!,
            width: item.width!,
            actualConsumption: roundAmountKm(item.amount),
            layerNumber: Number(item.layerNumber),
        }))

        const machineCounterUpdates =
            machineCounter != null
                ? getUniqueMachines(printJob).map(machine => ({
                      machineDocumentId: machine.documentId,
                      machineCounter,
                  }))
                : undefined

        const submitResult = await submitConsumption({
            data: {
                printJobDocumentId: printJob.documentId,
                items,
                actualFloods: Math.trunc(values.actualFloods!),
                machineCounter,
                machineCounterUpdates,
            },
        })

        return Boolean(submitResult.data)
    }

    const onSave = async () => {
        let values: ConsumptionFormValues

        try {
            values = await form.validateFields()
        } catch {
            return
        }

        const nextMeters = materialLengthKmToMeters(
            sumMaterialConsumptionLengthKm(getValidMaterialConsumptions(values.materials))
        )
        const currentMachineCounter = toMeters(getUniqueMachines(printJob)[0]?.machineCounter)
        const initialForCounter =
            printJobMachineCounterRef.current > 0 ? initialConsumptionMetersRef.current : 0

        const newMachineCounter = computeMachineCounterAfterSave(
            currentMachineCounter,
            initialForCounter,
            nextMeters
        )

        if (!(await saveConsumption(values, newMachineCounter ?? undefined))) return

        onSuccess()
        onClose()
    }

    if (!editable) {
        return <div>Расход уже сохранён. Редактирование недоступно.</div>
    }

    return (
        <Form form={form} layout="vertical" initialValues={getInitialValues(printJob)}>
            <Form.Item
                name="actualFloods"
                label="Кол-во ручьев фактическое"
                rules={[
                    { required: true, message: 'Укажите количество ручьев' },
                    {
                        type: 'number',
                        min: 1,
                        transform: value => (value == null ? value : Number(value)),
                        message: 'Укажите целое число не меньше 1',
                    },
                ]}
            >
                <InputNumber min={1} precision={0} style={{ width: '100%' }} />
            </Form.Item>

            <MaterialsList typeOptions={typeOptions} types={types} />

            <MachineCounterPreview />

            <HiddenMachineCountersFields />

            <Space style={{ marginTop: 16 }}>
                <Button type="primary" loading={submitState.isLoading} onClick={onSave}>
                    Сохранить
                </Button>
            </Space>
        </Form>
    )
}

export default ConsumptionForm